/**
 * Showroom Appointment Booking
 * Multi-step wizard: Date → Time → Contact → Confirmation
 * Submits via Shopify's native /contact endpoint (sends email to store admin).
 */

class ShowroomAppointment {
  constructor(section) {
    this.section = section;
    this.currentStep = 1;
    this.selectedDate = null;
    this.selectedTime = null;
    this.currentCalendarMonth = new Date();
    this.currentCalendarMonth.setDate(1);
    this.currentViewDate = null;

    this.init();
  }

  // ─── Static data ────────────────────────────────────────────────────────────


  static MONTHS = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
  ];

  static MONTHS_SHORT = [
    'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
  ];

  static DAYS_LONG = [
    'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag',
  ];

  static DAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  // ─── Date utilities ──────────────────────────────────────────────────────────

  toISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  }

  startOfToday() {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }

  sameDay(a, b) {
    return (
      a && b &&
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  isClosed(date) {
    const iso = this.toISO(date);
    return (this.blockedRanges || []).some(([from, to]) => iso >= from && iso <= to);
  }

  isAvailable(date) {
    if (date < this.startOfToday()) return false;
    if (this.isClosed(date)) return false;
    // A special date entry makes a normally-closed day bookable
    const iso = this.toISO(date);
    const special = (this.specialDates || []).find(s => s.date === iso);
    if (special) return special.slots && special.slots.trim().length > 0;
    return (this.availableDays || new Set([2, 3, 4, 5, 6])).has(date.getDay());
  }

  // ─── Time slot logic ─────────────────────────────────────────────────────────

  /**
   * Returns array of "HH:MM" strings for the given date.
   * Opening hours and slot interval are read from section dataset (Shopify settings).
   */
  getSlotsForDate(date) {
    // Check for a special date override first
    const iso = this.toISO(date);
    const special = (this.specialDates || []).find(s => s.date === iso);
    if (special) return this._generateSlots(special.slots || '');

    const dow = date.getDay();
    const rangesStr = dow === 6
      ? (this.slotConfig ? this.slotConfig.saturday : '09:00-16:00')
      : (this.slotConfig ? this.slotConfig.weekdays : '09:00-12:00, 13:30-18:00');
    return this._generateSlots(rangesStr);
  }

  /**
   * Parses a comma-separated opening hours string (e.g. "09:00-12:00, 13:30-18:00")
   * and returns start-time slots at slotInterval spacing.
   * The last bookable start time = closing time − appointment duration.
   */
  _generateSlots(rangesStr) {
    if (!rangesStr) return [];
    const duration = parseInt(this.section.dataset.duration || '60', 10);
    const interval = this.slotInterval || 30;
    const slots = [];
    rangesStr.split(',').forEach(range => {
      const parts = range.trim().split('-');
      if (parts.length < 2) return;
      const [sh, sm] = parts[0].trim().split(':').map(Number);
      const [eh, em] = parts[1].trim().split(':').map(Number);
      const lastMin = eh * 60 + em - duration;
      let h = sh, m = sm;
      while (h * 60 + m <= lastMin) {
        slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        m += interval;
        if (m >= 60) { h += 1; m -= 60; }
      }
    });
    return slots;
  }

  // ─── Calendar rendering ──────────────────────────────────────────────────────

  renderCalendar() {
    const year = this.currentCalendarMonth.getFullYear();
    const month = this.currentCalendarMonth.getMonth();
    const today = this.startOfToday();
    const now = new Date();

    // Header label
    this.qs('[data-calendar-month]').textContent =
      `${ShowroomAppointment.MONTHS[month]} ${year}`;

    // Prev / next month labels and disabled state
    const prevMonth = new Date(year, month - 1, 1);
    const nextMonth = new Date(year, month + 1, 1);

    this.qs('[data-calendar-prev-label]').textContent =
      ShowroomAppointment.MONTHS_SHORT[prevMonth.getMonth()].toUpperCase();
    this.qs('[data-calendar-next-label]').textContent =
      ShowroomAppointment.MONTHS_SHORT[nextMonth.getMonth()].toUpperCase();

    const prevBtn = this.qs('[data-calendar-prev]');
    prevBtn.disabled =
      year < now.getFullYear() ||
      (year === now.getFullYear() && month <= now.getMonth());

    // Build grid
    const grid = this.qs('[data-calendar-grid]');
    grid.innerHTML = '';

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Monday-first offset (Mon=0 … Sun=6)
    let offset = firstDay.getDay() - 1;
    if (offset < 0) offset = 6;

    for (let i = 0; i < offset; i++) {
      const filler = document.createElement('div');
      filler.className = 'sa-day sa-day--empty';
      filler.setAttribute('aria-hidden', 'true');
      grid.appendChild(filler);
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const cellDate = new Date(year, month, d);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = String(d).padStart(2, '0');
      btn.setAttribute('data-date', this.toISO(cellDate));

      if (this.isAvailable(cellDate)) {
        btn.className = 'sa-day sa-day--available';
        if (this.sameDay(cellDate, today)) btn.classList.add('sa-day--today');
        if (this.sameDay(cellDate, this.selectedDate)) btn.classList.add('sa-day--selected');
        btn.setAttribute(
          'aria-label',
          `${d}. ${ShowroomAppointment.MONTHS[month]} ${year}`
        );
        btn.addEventListener('click', () => this.onDateSelected(cellDate));
      } else {
        btn.className = 'sa-day sa-day--disabled';
        btn.disabled = true;
        btn.setAttribute('aria-label',
          `${d}. ${ShowroomAppointment.MONTHS[month]} ${year} – nicht verfügbar`);
      }

      grid.appendChild(btn);
    }
  }

  onDateSelected(date) {
    this.selectedDate = date;
    this.selectedTime = null;
    this.currentViewDate = date;
    this.renderCalendar();
    this.renderTimeSlots();
    this.goToStep(2);
  }

  // ─── Time slots rendering ────────────────────────────────────────────────────

  renderTimeSlots() {
    const date = this.currentViewDate;
    const dow = date.getDay();

    // Day label
    const label = `${ShowroomAppointment.DAYS_LONG[dow]}, ` +
      `${String(date.getDate()).padStart(2, '0')}.` +
      `${String(date.getMonth() + 1).padStart(2, '0')}.` +
      `${date.getFullYear()}`;
    this.qs('[data-time-day-label]').textContent = label;

    // Prev / next available day navigation
    const prevDay = this.findAdjacentAvailable(date, -1);
    const nextDay = this.findAdjacentAvailable(date, 1);

    const prevBtn = this.qs('[data-time-prev]');
    const nextBtn = this.qs('[data-time-next]');

    this.qs('[data-time-prev-label]').textContent = prevDay
      ? `${ShowroomAppointment.DAYS_SHORT[prevDay.getDay()]}, ` +
        `${String(prevDay.getDate()).padStart(2, '0')}.` +
        `${String(prevDay.getMonth() + 1).padStart(2, '0')}.`
      : '';
    prevBtn.disabled = !prevDay;

    this.qs('[data-time-next-label]').textContent = nextDay
      ? `${ShowroomAppointment.DAYS_SHORT[nextDay.getDay()]}, ` +
        `${String(nextDay.getDate()).padStart(2, '0')}.` +
        `${String(nextDay.getMonth() + 1).padStart(2, '0')}.`
      : '';
    nextBtn.disabled = !nextDay;

    // Build slot buttons
    const grid = this.qs('[data-time-slots-grid]');
    grid.innerHTML = '';

    const slots = this.getSlotsForDate(date);

    if (slots.length === 0) {
      const msg = document.createElement('p');
      msg.textContent = 'Für diesen Tag sind leider keine Termine verfügbar.';
      msg.style.cssText = 'color:var(--sa-muted);grid-column:1/-1;padding:0.5rem 0';
      grid.appendChild(msg);
      return;
    }

    slots.forEach((time) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sa-slot';
      btn.textContent = time;
      btn.setAttribute('data-time', time);
      btn.setAttribute('aria-label', `${time} Uhr`);

      if (this.sameDay(date, this.selectedDate) && time === this.selectedTime) {
        btn.classList.add('sa-slot--selected');
      }

      btn.addEventListener('click', () => this.onTimeSelected(date, time));
      grid.appendChild(btn);
    });
  }

  findAdjacentAvailable(date, direction) {
    const today = this.startOfToday();
    let candidate = this.addDays(date, direction);

    for (let i = 0; i < 90; i++) {
      if (direction < 0 && candidate < today) return null;
      if (this.isAvailable(candidate)) return candidate;
      candidate = this.addDays(candidate, direction);
    }
    return null;
  }

  onTimeSelected(date, time) {
    this.selectedDate = date;
    this.selectedTime = time;
    this.renderTimeSlots();
    this.renderSummary();
    this.goToStep(3);
  }

  // ─── Summary card ────────────────────────────────────────────────────────────

  renderSummary() {
    const summary = this.qs('[data-summary]');
    if (!this.selectedDate || !this.selectedTime) {
      summary.innerHTML = '';
      return;
    }

    const serviceName = this.section.dataset.serviceName || 'Persönliche Beratung im Showroom';
    const duration = this.section.dataset.duration || '60';
    const dateStr =
      `${String(this.selectedDate.getDate()).padStart(2, '0')}.` +
      `${String(this.selectedDate.getMonth() + 1).padStart(2, '0')}.` +
      `${this.selectedDate.getFullYear()}`;

    summary.innerHTML = `
      <div class="sa-summary__item">
        <div class="sa-summary__icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>
        <div class="sa-summary__info">
          <span class="sa-summary__name">${escapeHtml(serviceName)}</span>
          <span class="sa-summary__sub">Wohnreich Showroom, Rapperswil-Jona</span>
        </div>
      </div>
      <div class="sa-summary__item">
        <div class="sa-summary__icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div class="sa-summary__info">
          <span class="sa-summary__name">${escapeHtml(dateStr)}</span>
          <span class="sa-summary__sub">${escapeHtml(this.selectedTime)} Uhr · ${escapeHtml(duration)} min</span>
        </div>
      </div>
    `;
  }

  // ─── Step navigation ─────────────────────────────────────────────────────────

  goToStep(step) {
    const previousStep = this.currentStep;
    this.currentStep = step;

    const outgoing = this.section.querySelector(`[data-step="${previousStep}"]`);
    const incoming = this.section.querySelector(`[data-step="${step}"]`);

    if (!incoming) return;

    // Update stepper UI immediately (before animation)
    this.section.querySelectorAll('[data-step-indicator]').forEach((el) => {
      const n = parseInt(el.dataset.stepIndicator, 10);
      el.classList.remove('sa-stepper__step--active', 'sa-stepper__step--done');
      if (n === step) el.classList.add('sa-stepper__step--active');
      else if (n < step) el.classList.add('sa-stepper__step--done');
    });
    this.section.querySelectorAll('[data-connector]').forEach((el) => {
      const n = parseInt(el.dataset.connector, 10);
      el.classList.toggle('sa-stepper__line--done', step > n);
    });

    const showIncoming = () => {
      incoming.hidden = false;
      void incoming.offsetWidth; // force reflow so animation triggers
      incoming.classList.add('sa-step--entering');
      incoming.addEventListener('animationend', () => {
        incoming.classList.remove('sa-step--entering');
      }, { once: true });
    };

    // Sequential: exit finishes fully before enter begins — no overlap
    if (outgoing && !outgoing.hidden) {
      outgoing.classList.add('sa-step--exiting');
      outgoing.addEventListener('animationend', () => {
        outgoing.hidden = true;
        outgoing.classList.remove('sa-step--exiting');
        showIncoming();
      }, { once: true });
    } else {
      showIncoming();
    }

    this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─── Form submission ─────────────────────────────────────────────────────────

  async submitForm() {
    const submitBtn = this.qs('[data-submit-btn]');
    const errorEl = this.qs('[data-form-error]');

    // Collect values
    const firstName = this.qs('[data-field-firstname]').value.trim();
    const lastName = this.qs('[data-field-lastname]').value.trim();
    const email = this.qs('[data-field-email]').value.trim();
    const phone = this.qs('[data-field-phone]').value.trim();
    const message = this.qs('[data-field-message]').value.trim();
    const newsletter = this.qs('[data-field-newsletter]').checked;

    // Field validation
    const required = [
      { el: this.qs('[data-field-firstname]'), val: firstName },
      { el: this.qs('[data-field-lastname]'),  val: lastName },
      { el: this.qs('[data-field-email]'),     val: email },
      { el: this.qs('[data-field-phone]'),     val: phone },
    ];

    let valid = true;
    required.forEach(({ el, val }) => {
      if (!val) {
        el.classList.add('is-error');
        valid = false;
      } else {
        el.classList.remove('is-error');
      }
    });

    if (!valid) {
      this.showError(errorEl, 'Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.qs('[data-field-email]').classList.add('is-error');
      this.showError(errorEl, 'Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      return;
    }

    errorEl.style.display = 'none';

    // Format the email body
    const serviceName = this.section.dataset.serviceName || 'Persönliche Beratung im Showroom';
    const duration = this.section.dataset.duration || '60';
    const dateStr =
      `${String(this.selectedDate.getDate()).padStart(2, '0')}.` +
      `${String(this.selectedDate.getMonth() + 1).padStart(2, '0')}.` +
      `${this.selectedDate.getFullYear()}`;

    const bodyLines = [
      `── Terminanfrage Showroom ──────────────────`,
      `Beratungsart: ${serviceName}`,
      `Datum:        ${dateStr}`,
      `Uhrzeit:      ${this.selectedTime} Uhr`,
      `Dauer:        ${duration} Minuten`,
      ``,
      `── Kontaktdaten ────────────────────────────`,
      `Name:         ${firstName} ${lastName}`,
      `E-Mail:       ${email}`,
      `Telefon:      ${phone}`,
    ];

    if (message) {
      bodyLines.push(``, `── Nachricht ────────────────────────────────`, message);
    }

    bodyLines.push(``, `── Einstellungen ───────────────────────────`);
    bodyLines.push(`Newsletter:   ${newsletter ? 'Ja' : 'Nein'}`);

    const body = bodyLines.join('\n');

    // Loading state (stays on during page navigation — intentional)
    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');
    submitBtn.setAttribute('aria-busy', 'true');

    // The contact form wraps the whole section — same element as the wizard.
    const contactForm = this.qs('.sa-contact-form');

    // Populate the hidden contact[*] inputs that live inside step 3.
    const setField = (key, val) => {
      const el = contactForm?.querySelector(`[data-sa-field="${key}"]`);
      if (el) el.value = val;
    };
    setField('email', email);
    setField('name',  `${firstName} ${lastName}`);
    setField('phone', phone);
    setField('body',  body);

    // Submit via captcha.protect so hCaptcha can inject h-captcha-response.
    // On success Shopify redirects to the same page; Liquid renders the
    // success screen via form.posted_successfully? — no JS detection needed.
    const doSubmit = () => {
      contactForm.setAttribute('target', '_self');
      contactForm.submit();
    };

    if (window.Shopify?.captcha?.protect) {
      window.Shopify.captcha.protect(contactForm, doSubmit);
    } else {
      doSubmit();
    }
  }

  showError(errorEl, msg) {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
    errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  qs(selector) {
    return this.section.querySelector(selector);
  }

  /**
   * Parses blocked date ranges from the inline JSON script block.
   * One entry per line, either:
   *   "YYYY-MM-DD"                        single day
   *   "YYYY-MM-DD - YYYY-MM-DD"           range
   * Tolerates Windows line endings and hyphen / en-dash / em-dash separators.
   */
  _parseBlockedDates() {
    const el = this.qs('[data-blocked-dates]');
    if (!el) return [];

    let raw = '';
    try {
      raw = JSON.parse((el.textContent || '').trim() || '""') || '';
    } catch (e) {
      return [];
    }

    const isoRe = /^\d{4}-\d{2}-\d{2}$/;
    const lines = raw
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean);

    const ranges = [];
    for (const line of lines) {
      const parts = line.split(/\s*[-\u2013\u2014]\s*/);
      if (parts.length === 6) {
        const from = `${parts[0]}-${parts[1]}-${parts[2]}`;
        const to   = `${parts[3]}-${parts[4]}-${parts[5]}`;
        if (isoRe.test(from) && isoRe.test(to)) ranges.push([from, to]);
      } else if (parts.length === 3) {
        const single = `${parts[0]}-${parts[1]}-${parts[2]}`;
        if (isoRe.test(single)) ranges.push([single, single]);
      }
    }
    return ranges;
  }

  // ─── Initialization ──────────────────────────────────────────────────────────

  init() {
    // When form.posted_successfully? is true, Liquid renders only the success
    // screen — the wizard DOM elements don't exist. Nothing to initialise.
    if (!this.qs('[data-step]')) return;

    // Parse available weekdays (comma-separated day numbers: 0=Sun … 6=Sat)
    this.availableDays = new Set(
      (this.section.dataset.availableDays || '2,3,4,5,6')
        .split(',').filter(Boolean).map(Number)
    );

    this.blockedRanges = this._parseBlockedDates();

    // Parse slot interval and opening-hours config
    this.slotInterval = parseInt(this.section.dataset.slotInterval || '30', 10);
    this.slotConfig = {
      weekdays: this.section.dataset.slotsWeekdays || '09:00-12:00, 13:30-18:00',
      saturday: this.section.dataset.slotsSaturday || '09:00-16:00',
    };

    // Parse special date overrides from inline JSON (section blocks)
    const _specialEl = this.qs('[data-special-dates]');
    try {
      this.specialDates = _specialEl ? JSON.parse(_specialEl.textContent) : [];
    } catch (e) {
      this.specialDates = [];
    }

    // Calendar: initial render
    this.renderCalendar();

    // Calendar: month navigation
    this.qs('[data-calendar-prev]').addEventListener('click', () => {
      this.currentCalendarMonth = new Date(
        this.currentCalendarMonth.getFullYear(),
        this.currentCalendarMonth.getMonth() - 1,
        1
      );
      this.renderCalendar();
    });

    this.qs('[data-calendar-next]').addEventListener('click', () => {
      this.currentCalendarMonth = new Date(
        this.currentCalendarMonth.getFullYear(),
        this.currentCalendarMonth.getMonth() + 1,
        1
      );
      this.renderCalendar();
    });

    // Time slots: day navigation
    this.qs('[data-time-prev]').addEventListener('click', () => {
      const prev = this.findAdjacentAvailable(this.currentViewDate, -1);
      if (prev) {
        this.currentViewDate = prev;
        this.renderTimeSlots();
      }
    });

    this.qs('[data-time-next]').addEventListener('click', () => {
      const next = this.findAdjacentAvailable(this.currentViewDate, 1);
      if (next) {
        this.currentViewDate = next;
        this.renderTimeSlots();
      }
    });

    // Back buttons
    this.section.querySelectorAll('[data-back-to]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.goToStep(parseInt(btn.dataset.backTo, 10));
      });
    });

    // Clicking a completed stepper step navigates back to it
    this.section.querySelectorAll('[data-step-indicator]').forEach((el) => {
      el.addEventListener('click', () => {
        const n = parseInt(el.dataset.stepIndicator, 10);
        if (el.classList.contains('sa-stepper__step--done')) {
          this.goToStep(n);
        }
      });
    });

    // Submit
    this.qs('[data-submit-btn]').addEventListener('click', () => {
      this.submitForm();
    });

    // Clear field errors on input
    this.section
      .querySelectorAll('[data-field-firstname],[data-field-lastname],[data-field-email],[data-field-phone]')
      .forEach((input) => {
        input.addEventListener('input', () => {
          input.classList.remove('is-error');
          const errorEl = this.qs('[data-form-error]');
          if (errorEl && !this.section.querySelector('.is-error')) {
            errorEl.style.display = 'none';
          }
        });
      });
  }
}

// ─── Utility ───────────────────────────────────────────────────────────────────

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ─── Bootstrap ─────────────────────────────────────────────────────────────────

function initShowroomSections(root) {
  root.querySelectorAll('[data-showroom-section]').forEach((section) => {
    if (!section.dataset.saReady) {
      section.dataset.saReady = 'true';
      new ShowroomAppointment(section);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => initShowroomSections(document));

// Shopify theme editor live reload support
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-showroom-section]').forEach((section) => {
    delete section.dataset.saReady;
  });
  initShowroomSections(event.target);
});
